'use strict';
/* ============================================================================
 * tcg-filter.js — DOM controller for the TCG set/rarity filter.
 *
 * Reads the real product-card <li>s the section renders, background-indexes the
 * rest of the collection via the Section Rendering API, then delegates ALL
 * classification/grouping/ordering to tcg-model.js (computePlan/facetCounts/
 * matches) and rebuilds the grid by MOVING the real card nodes into era bands +
 * set shelves. Two modes (Sealed | Singles), reactive facets, chips, era
 * jump-strip, URL sync, mobile drawer, a11y live region. Scoped per [data-tcgf]
 * so multiple instances never collide.
 * ========================================================================== */
(function () {
  var M = window.TCGModel;
  if (!M) return;

  function ce(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); }
  function modeOf(it) { return it.mode || M.classifyMode(it); }
  var CHASE = { 'Promo': 1, 'Hyper Rare': 1, 'Secret Rare': 1, 'Rainbow Rare': 1, 'Special Illustration Rare': 1, 'Illustration Rare': 1, 'Ultra Rare': 1, 'Full Art': 1, 'Amazing Rare': 1, 'Shiny Rare': 1, 'Trainer Gallery': 1, 'Double Rare': 1 };
  var DEBOUNCE = 220;

  function Ctl(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId || '';
    this.grid = root.querySelector('[data-tcgf-grid]');
    this.facetsEl = root.querySelector('[data-tcgf-facets]');
    this.chipsEl = root.querySelector('[data-tcgf-chips]');
    this.erabar = root.querySelector('[data-tcgf-erabar]');
    this.countEl = root.querySelector('[data-tcgf-count]');
    this.liveEl = root.querySelector('[data-tcgf-live]');
    this.badgeEl = root.querySelector('[data-tcgf-badge]');
    this.searchEl = root.querySelector('[data-tcgf-search]');
    this.nomatchEl = root.querySelector('[data-tcgf-nomatch]');
    this.loadingEl = root.querySelector('[data-tcgf-loading]');
    this.emptyEl = root.querySelector('[data-tcgf-empty]');
    this.scrimEl = root.querySelector('[data-tcgf-scrim]');
    this.cfg = this.parseConfig();
    if (this.cfg.eraOrder && M.setEraOrder) M.setEraOrder(this.cfg.eraOrder);
    this.cols = this.cfg.columns || 5;
    this.state = {}; this.cfg.axes.forEach(function (a) { this.state[a.key] = new Set(); }, this);
    this.mode = 'sealed';
    this.expanded = {};
    this.facetSearch = '';
    this.byId = {};
    this.items = [];
    this.indexed = false;
    this.urlTimer = null;
    this.firstRender = true;
  }

  Ctl.prototype.parseConfig = function () {
    var s = this.root.querySelector('[data-tcgf-config]');
    var cfg = { axes: [], shelfCap: 4, shelfCapMin: 7, setShelfMin: 3, columns: 5 };
    if (s) { try { cfg = JSON.parse(s.textContent); } catch (e) { /* keep defaults */ } }
    if (!cfg.axes || !cfg.axes.length) {
      cfg.axes = [{ key: 'game', param: 'game', label: 'Game', kind: 'enum', reactive: 'hide-single' },
        { key: 'era', param: 'era', label: 'Series / Era', kind: 'enum' },
        { key: 'set', param: 'set', label: 'Set', kind: 'enum' },
        { key: 'rarity', param: 'rarity', label: 'Rarity', kind: 'ordered', order: 'rarest-first', appliesTo: ['slab', 'single'] },
        { key: 'format', param: 'fmt', label: 'Format', kind: 'enum' }];
    }
    return cfg;
  };
  Ctl.prototype.axis = function (key) { for (var i = 0; i < this.cfg.axes.length; i++) if (this.cfg.axes[i].key === key) return this.cfg.axes[i]; return null; };
  Ctl.prototype.listClass = function () { return 'tcgf__list grid grid--' + this.cols + '-col-desktop grid--2-col-tablet-down'; };
  Ctl.prototype.gameContext = function () {
    var g = this.state.game && this.state.game.size === 1 ? Array.from(this.state.game)[0] : null;
    if (g) return g;
    return (this.items[0] && this.items[0].game) || 'Pokemon';
  };

  // ---- indexing ------------------------------------------------------------
  Ctl.prototype.adopt = function (node) {
    var id = node.getAttribute('data-id'); if (!id || this.byId[id]) return null;
    var d = node.dataset;
    var it = { id: id, node: node, title: d.title || '', game: d.game || '', era: d.era || '',
      set: d.set || '', rarity: d.rarity || '', format: d.format || '', year: d.year || '',
      release: d.release || '' };
    it.mode = M.classifyMode(it); // derive (sealed/slab/single/other) — don't trust a stale data-mode
    this.byId[id] = it; this.items.push(it); return it;
  };
  Ctl.prototype.collect = function (scope) {
    var nodes = scope.querySelectorAll('[data-tcgf-item]'), added = 0;
    for (var i = 0; i < nodes.length; i++) { if (this.adopt(nodes[i])) added++; }
    return added;
  };

  Ctl.prototype.indexRemaining = function () {
    // Prefer the TRUE server page count (paginate.pages) over our estimate — the
    // section paginates by whatever Shopify honours, not necessarily page_size.
    var pcEl = this.root.querySelector('[data-tcgf-pagecount]');
    var pages = (pcEl && parseInt(pcEl.getAttribute('data-tcgf-pagecount'), 10)) || parseInt(this.grid.getAttribute('data-pages'), 10) || 1;
    // Single page → the SSR already holds the WHOLE collection: nothing to fetch,
    // everything is ready for instant, complete filtering.
    if (pages <= 1 || !this.sectionId) { this.indexed = true; return; }
    var self = this, path = window.location.pathname, CONC = 5;
    if (this.loadingEl) this.loadingEl.hidden = false;
    // Preserve only the template selector (?view=) — NOT active filters — so the
    // section resolves when previewing via ?view=, and the FULL catalogue is indexed.
    var view = ''; try { view = new URLSearchParams(window.location.search).get('view') || ''; } catch (e) {}
    // Section renders are CDN-cached by URL (Shopify invalidates on data change);
    // we lean on that cache since this collection paginates in many small pages.
    function fetchPage(p, tries) {
      var url = path + '?section_id=' + encodeURIComponent(self.sectionId) + '&page=' + p + '&sort_by=title-descending' + (view ? '&view=' + encodeURIComponent(view) : '');
      return fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
        .then(function (r) { if (!r.ok) throw new Error('page ' + p + ' ' + r.status); return r.text(); })
        .catch(function () { return (tries || 0) < 1 ? fetchPage(p, (tries || 0) + 1) : ''; });
    }
    function absorb(html) {
      if (!html) return;
      var nodes = new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-tcgf-item]');
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i], id = n.getAttribute('data-id');
        if (!id || self.byId[id]) continue;
        var imported = document.adoptNode(n);
        self.adopt(imported); self.pen.appendChild(imported);
      }
    }
    // Title-descending tiles the whole catalogue. Fetch in gentle waves (≤ CONC
    // concurrent) so many-page collections never exceed the browser connection
    // cap and stall; relayout progressively as each wave lands.
    function wave(start) {
      if (start > pages) { self.indexed = true; if (self.loadingEl) self.loadingEl.hidden = true; self.render(); self._indexing = false; if (self.liveEl && self._liveText) self.liveEl.textContent = self._liveText; setTimeout(function () { self.saveCache(); }, 400); return; }
      var jobs = [];
      for (var p = start; p < start + CONC && p <= pages; p++) jobs.push(fetchPage(p));
      Promise.all(jobs).then(function (htmls) { htmls.forEach(absorb); self.render(); wave(start + CONC); });
    }
    this._indexing = true;
    wave(1);
  };

  // ---- session cache -------------------------------------------------------
  // After the first full index, remember the WHOLE catalogue so any later visit
  // in the same browsing session paints every product instantly — no re-fetch,
  // no pop-in. Keyed by collection path + page count so it self-invalidates when
  // the catalogue size changes; sessionStorage keeps it naturally fresh.
  Ctl.prototype.cacheKey = function () { return 'tcgf:v2:' + window.location.pathname; };
  Ctl.prototype.pageCount = function () { var el = this.root.querySelector('[data-tcgf-pagecount]'); return el ? (el.getAttribute('data-tcgf-pagecount') || '') : ''; };
  Ctl.prototype.saveCache = function () {
    try {
      if (!window.sessionStorage) return;
      var html = '', b = this.byId, k;
      for (k in b) { var n = b[k].node.cloneNode(true); n.removeAttribute('style'); html += n.outerHTML; }
      sessionStorage.setItem(this.cacheKey(), JSON.stringify({ pc: this.pageCount(), n: this.items.length, html: html }));
    } catch (e) { /* quota exceeded / storage disabled — cache is best-effort */ }
  };
  Ctl.prototype.loadCache = function () {
    try {
      if (!window.sessionStorage) return false;
      var raw = sessionStorage.getItem(this.cacheKey()); if (!raw) return false;
      var c = JSON.parse(raw); if (!c || !c.html || c.pc !== this.pageCount()) return false; // catalogue changed → re-index
      var nodes = new DOMParser().parseFromString('<div>' + c.html + '</div>', 'text/html').querySelectorAll('[data-tcgf-item]');
      var added = 0;
      for (var i = 0; i < nodes.length; i++) {
        var id = nodes[i].getAttribute('data-id'); if (!id || this.byId[id]) continue;
        var imported = document.adoptNode(nodes[i]);
        if (this.adopt(imported)) { this.pen.appendChild(imported); added++; }
      }
      return added > 0;
    } catch (e) { return false; }
  };

  // ---- view helpers --------------------------------------------------------
  Ctl.prototype.viewItems = function () {
    var m = this.mode;
    return this.items.filter(function (it) {
      var im = modeOf(it);
      if (m === 'single') return im === 'single';
      if (m === 'slab') return im === 'slab';
      return im === 'sealed' || im === 'other'; // Sealed tab also holds accessories
    });
  };
  Ctl.prototype.eraId = function (era) { return 'tcgf-' + this.sectionId + '-era-' + String(era || 'more').replace(/\W+/g, '') || 'x'; };

  // ---- render orchestration ------------------------------------------------
  Ctl.prototype.render = function () {
    var view = this.viewItems();
    var plan = M.computePlan(view, this.state, this.cfg);
    // Facets: full rebuild only when the option UNIVERSE changes (mode / game
    // visibility). On a plain filter toggle, update counts + checked IN PLACE so
    // the sidebar row you clicked never moves or flickers.
    // Fold the option-universe size per axis into the signature so background
    // indexing that reveals NEW eras/sets/rarities rebuilds the facet rows (values
    // are only added, never swapped). Plain filter toggles leave it unchanged → in-place.
    var uni = this.facetUniverse(view);
    var sig = this.mode + '|' + (M.gameFacetHidden(this.items) ? '0' : '1') + '|' +
      this.axesForMode().map(function (ax) { return (uni[ax.key] || []).length; }).join('.');
    if (sig !== this._facetSig || !this.facetRows) { this._facetSig = sig; this.buildFacets(view); }
    else this.syncFacets(view);
    this.buildGridAnimated(plan);
    this.renderChips();
    this.renderErabar(plan);
    this.updateCount(plan, view);
    this.updateModeTabs();
    if (this.firstRender) { this.firstRender = false; this.root.setAttribute('data-js-active', 'true'); this.root.setAttribute('data-engine', 'on'); }
  };

  // A tab whose mode has nothing in the ENTIRE catalogue (not just the current
  // filter view) is dead weight — e.g. an all-sealed game has no Slabs/Singles.
  // Hide those tabs; if only one mode remains (and we're on it), drop the tablist.
  Ctl.prototype.updateModeTabs = function () {
    var counts = { sealed: 0, slab: 0, single: 0 };
    for (var i = 0; i < this.items.length; i++) { var m = this.items[i].mode; if (counts[m] != null) counts[m]++; }
    var vis = 0;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-tcgf-mode]'), function (b) {
      var on = counts[b.getAttribute('data-tcgf-mode')] > 0;
      b.style.display = on ? '' : 'none'; if (on) vis++;
    });
    var wrap = this.root.querySelector('[data-tcgf-modes]');
    if (wrap) wrap.classList.toggle('tcgf__modes--solo', vis < 2 && this.mode === 'sealed');
  };

  // Rebuild the grid, preserving scroll position and FLIP-animating cards that
  // move (and fading in new ones) so filtering never snaps or jumps the page.
  Ctl.prototype.buildGridAnimated = function (plan) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // FLIP (move + fade entering) only for user filter changes; fade newly-indexed
    // cards during background loading; mode switches crossfade the whole mount (setMode).
    var flip = !reduce && this.indexed && !this.firstRender && !this._modeSwitch && !this._indexing;
    var fadeNew = !reduce && !this.firstRender && !flip && !this._modeSwitch;
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var vh = window.innerHeight || 800;
    function near(r) { return r.bottom >= -60 && r.top <= vh + 60; }
    var first = null;
    if (flip) {
      first = {};
      var cur = this.mount.querySelectorAll('[data-tcgf-item]');
      // Snap any in-flight FLIP transforms back to rest BEFORE measuring, so a rapid
      // re-filter mid-animation records true positions (never a mid-transform jump).
      for (var k = 0; k < cur.length; k++) { var cs = cur[k].style; if (cs.transform) { cs.transition = 'none'; cs.transform = ''; cs.opacity = ''; cur[k].__fin = 0; } }
      for (var i = 0; i < cur.length; i++) {
        var r0 = cur[i].getBoundingClientRect();
        if (r0.bottom < -280 || r0.top > vh + 280) continue;
        var id0 = cur[i].getAttribute('data-id'); if (id0) first[id0] = r0;
      }
    }
    this.buildGrid(plan);
    if (!this.firstRender) { try { window.scrollTo(0, y); } catch (e) {} }
    // If the content shrank, scrollTo was clamped — subtract that shift so FLIP
    // deltas reflect only real reflow motion, not the scroll jump.
    var sd = (window.pageYOffset || document.documentElement.scrollTop || 0) - y;
    this._seen = this._seen || {};
    var last = this.mount.querySelectorAll('[data-tcgf-item]');
    if (reduce || this.firstRender) {
      for (var a = 0; a < last.length; a++) { var ida = last[a].getAttribute('data-id'); if (ida) this._seen[ida] = 1; }
      return;
    }
    var play = [];
    for (var j = 0; j < last.length; j++) {
      var node = last[j], id = node.getAttribute('data-id'); if (!id) continue;
      var wasSeen = this._seen[id]; this._seen[id] = 1;
      var f = first && first[id];
      if (f) {
        var l = node.getBoundingClientRect(), dx = f.left - l.left, dy = f.top - l.top - sd;
        if (dx || dy) { node.style.transition = 'none'; node.style.transform = 'translate(' + dx + 'px,' + dy + 'px)'; play.push(node); }
      } else if (flip) { // card entering the view via a filter change
        var lr = node.getBoundingClientRect();
        if (near(lr)) { node.style.transition = 'none'; node.style.opacity = '0'; node.__fin = 1; play.push(node); }
      } else if (fadeNew && !wasSeen) { // freshly-indexed card
        var lr2 = node.getBoundingClientRect();
        if (near(lr2)) { node.style.transition = 'none'; node.style.opacity = '0'; node.__fin = 1; play.push(node); }
      }
    }
    if (!play.length) return;
    requestAnimationFrame(function () { requestAnimationFrame(function () {
      for (var k = 0; k < play.length; k++) {
        var n = play[k];
        n.style.transition = 'transform .26s cubic-bezier(.2,.7,.3,1), opacity .24s ease';
        n.style.transform = ''; if (n.__fin) { n.style.opacity = ''; n.__fin = 0; }
      }
    }); });
  };

  Ctl.prototype.buildGrid = function (plan) {
    var b = this.byId, pen = this.pen, k;
    for (k in b) { if (b[k].node) pen.appendChild(b[k].node); } // detach all live nodes safely
    this.mount.innerHTML = '';
    var total = this.modeTotal(plan);
    if (total === 0) { this.showEmpty(); return; }
    this.emptyEl.hidden = true;
    var frag = document.createDocumentFragment();
    if (this.mode === 'single') this.buildRarityList(frag, plan.singles, 'Raw singles — rarest first');
    else if (this.mode === 'slab') this.buildRarityList(frag, plan.slabs, 'Graded slabs — rarest first');
    else {
      for (var i = 0; i < plan.eras.length; i++) this.buildEra(frag, plan.eras[i]);
      if (plan.other && plan.other.length) this.buildOther(frag, plan.other);
    }
    this.mount.appendChild(frag);
  };

  Ctl.prototype.shelfUl = function (items) {
    var ul = ce('ul', this.listClass()); ul.setAttribute('role', 'list');
    for (var i = 0; i < items.length; i++) ul.appendChild(items[i].node);
    return ul;
  };

  Ctl.prototype.buildEra = function (frag, era) {
    var self = this;
    var sec = ce('section', 'tcgf__era'); sec.setAttribute('data-tcgf-era', era.era || 'More'); sec.id = this.eraId(era.era);
    sec.appendChild(ce('h2', 'tcgf__era-head', esc(era.era || 'More series')));
    era.shelves.forEach(function (sh) {
      var wrap = ce('div', 'tcgf__shelf' + (sh.total <= 2 ? ' tcgf__shelf--sm' : '')); // compact header for tiny (box+pack) sets
      var head = ce('div', 'tcgf__shelf-head');
      head.appendChild(ce('span', 'tcgf__shelf-title', esc(sh.set || '')));
      head.appendChild(ce('span', 'tcgf__shelf-count', sh.total + ' item' + (sh.total === 1 ? '' : 's')));
      var open = !!self.expanded[sh.key];
      if (sh.total > sh.shown.length) {
        var vb = ce('button', 'tcgf__viewall', open ? 'Show less' : 'View all ' + sh.total); vb.type = 'button';
        vb.addEventListener('click', function () { self.expanded[sh.key] = !open; self.render(); });
        head.appendChild(vb);
      }
      wrap.appendChild(head);
      wrap.appendChild(self.shelfUl(open ? sh.items : sh.shown));
      sec.appendChild(wrap);
    });
    if (era.loose && era.loose.length) {
      var lw = ce('div', 'tcgf__shelf');
      lw.appendChild(ce('div', 'tcgf__shelf-head',
        '<span class="tcgf__shelf-title tcgf__shelf-title--muted">Other ' + esc(era.era || '') + ' sets</span><span class="tcgf__shelf-count">' + era.loose.length + '</span>'));
      lw.appendChild(this.shelfUl(era.loose));
      sec.appendChild(lw);
    }
    frag.appendChild(sec);
  };

  // Rarity-grouped list, rarest-first — shared by Singles (raw) and Slabs (graded).
  Ctl.prototype.buildRarityList = function (frag, arr, heading) {
    var sec = ce('section', 'tcgf__era');
    sec.appendChild(ce('h2', 'tcgf__era-head', esc(heading)));
    var cur = null, ul = null;
    for (var i = 0; i < arr.length; i++) {
      var it = arr[i], r = it.rarity || 'Other';
      if (r !== cur) { cur = r; sec.appendChild(ce('div', 'tcgf__rar-head' + (CHASE[r] ? ' is-chase' : ''), esc(r))); ul = ce('ul', this.listClass()); ul.setAttribute('role', 'list'); sec.appendChild(ul); }
      ul.appendChild(it.node);
    }
    frag.appendChild(sec);
  };
  Ctl.prototype.modeTotal = function (plan) {
    if (this.mode === 'single') return plan.singles.length;
    if (this.mode === 'slab') return plan.slabs.length;
    return plan.eras.reduce(function (n, e) { return n + e.total; }, 0) + (plan.other ? plan.other.length : 0);
  };

  Ctl.prototype.buildOther = function (frag, other) {
    var sec = ce('section', 'tcgf__era tcgf__era--other');
    sec.appendChild(ce('h2', 'tcgf__era-head', 'Accessories &amp; other'));
    sec.appendChild(this.shelfUl(other));
    frag.appendChild(sec);
  };

  // ---- empty state (smart) -------------------------------------------------
  Ctl.prototype.showEmpty = function () {
    var box = this.emptyEl; box.hidden = false;
    var hint = box.querySelector('[data-tcgf-empty-hint]'), fix = box.querySelector('[data-tcgf-empty-fix]');
    var self = this, best = null;
    this.cfg.axes.forEach(function (ax) {
      var sel = self.state[ax.key]; if (!sel || !sel.size) return;
      var saved = sel; self.state[ax.key] = new Set();
      var n = self.viewItems().filter(function (it) { return M.matches(it, self.state, self.cfg); }).length;
      self.state[ax.key] = saved;
      if (n > 0 && (!best || n > best.n)) best = { ax: ax, n: n };
    });
    if (hint && fix) {
      if (best) {
        hint.hidden = false; hint.textContent = 'Removing ' + best.ax.label + ' shows ' + best.n + ' product' + (best.n === 1 ? '' : 's') + '.';
        fix.hidden = false; fix.textContent = 'Remove ' + best.ax.label + ' filter';
        fix.onclick = function () { self.state[best.ax.key] = new Set(); self.expanded = {}; self.commit(); };
      } else { hint.hidden = true; fix.hidden = true; }
    }
  };

  // ---- facets --------------------------------------------------------------
  Ctl.prototype.axesForMode = function () {
    var out = [];
    if (!M.gameFacetHidden(this.items)) { var g = this.axis('game'); if (g) out.push(g); }
    var singleLike = this.mode === 'single' || this.mode === 'slab';
    var order = singleLike ? ['rarity', 'set', 'era'] : ['era', 'set', 'format'];
    for (var i = 0; i < order.length; i++) { var a = this.axis(order[i]); if (a) out.push(a); }
    return out;
  };
  // Full value list per axis for the current mode (independent of active filters,
  // so the row set is STABLE — options dim to 0 rather than appearing/vanishing).
  Ctl.prototype.facetUniverse = function (view) {
    var self = this, uni = {};
    this.axesForMode().forEach(function (ax) {
      var seen = {};
      for (var i = 0; i < view.length; i++) { var v = view[i][ax.key]; if (v !== '' && v != null) seen[v] = 1; }
      var list = Object.keys(seen);
      if (ax.key === 'rarity') list = M.orderRarities(self.gameContext(), list);
      else if (ax.key === 'era') list.sort(function (a, b) { return M.eraRank(a) - M.eraRank(b) || (a < b ? -1 : 1); });
      else list.sort();
      uni[ax.key] = list;
    });
    return uni;
  };
  Ctl.prototype.buildFacets = function (view) {
    var self = this, host = this.facetsEl; if (!host) return;
    host.innerHTML = ''; this.facetRows = {}; this.facetPod = {}; this.facetRest = {};
    this.userPodState = this.userPodState || {};
    var uni = this.facetUniverse(view), counts = M.facetCounts(view, this.state, this.cfg);
    this.axesForMode().forEach(function (ax) {
      var vals = uni[ax.key] || [];
      if (!vals.length && !(self.state[ax.key] && self.state[ax.key].size)) return;
      // Pods collapse by default (condensed); remember the user's manual open/close
      // across renders, and always keep a pod open when it has an active selection.
      var hasSel = !!(self.state[ax.key] && self.state[ax.key].size);
      var open = (self.userPodState[ax.key] != null) ? self.userPodState[ax.key] : hasSel;
      if (hasSel) open = true;
      self.facetRest[ax.key] = open;
      var wrap = ce('div', 'tcgf__facet'); wrap.setAttribute('data-open', open ? 'true' : 'false');
      var tg = ce('button', 'tcgf__facet-toggle'); tg.type = 'button'; tg.setAttribute('aria-expanded', open ? 'true' : 'false');
      tg.innerHTML = '<span class="tcgf__facet-label">' + esc(ax.label) + '</span>' +
        '<span class="tcgf__facet-meta"><span class="tcgf__facet-sum" data-sum hidden></span>' +
        '<span class="tcgf__chev" aria-hidden="true">▾</span></span>';
      var body = ce('div', 'tcgf__facet-body'), opts = ce('div', 'tcgf__facet-options'), rows = {};
      tg.addEventListener('click', function () {
        var o = wrap.getAttribute('data-open') === 'true';
        self.userPodState[ax.key] = !o; self.facetRest[ax.key] = !o;
        self.setPodOpen(ax.key, !o);
      });
      wrap.appendChild(tg);
      vals.forEach(function (v) {
        var checked = self.state[ax.key].has(v), c = (counts[ax.key] && counts[ax.key][v]) || 0;
        var row = ce('label', 'tcgf__opt'); row.setAttribute('data-checked', checked ? 'true' : 'false');
        if (c === 0 && !checked) row.setAttribute('data-disabled', 'true');
        var cb = ce('input'); cb.type = 'checkbox'; cb.checked = checked; cb.disabled = (c === 0 && !checked);
        cb.addEventListener('change', function () { if (cb.checked) self.state[ax.key].add(v); else self.state[ax.key]['delete'](v); self.expanded = {}; self.commit(); });
        row.appendChild(cb);
        row.appendChild(ce('span', 'tcgf__opt-name', esc(v)));
        var cEl = ce('span', 'tcgf__opt-count', String(c)); row.appendChild(cEl);
        opts.appendChild(row);
        rows[v] = { row: row, cb: cb, countEl: cEl };
      });
      opts.inert = !open; // collapsed panels are non-tabbable
      body.appendChild(opts); wrap.appendChild(body); host.appendChild(wrap);
      self.facetRows[ax.key] = rows;
      self.facetPod[ax.key] = { wrap: wrap, tg: tg, opts: opts, sumEl: tg.querySelector('[data-sum]') };
      self.updateFacetSummary(ax.key);
    });
    this.applyFacetSearch();
  };
  // Open/close one pod (data-open drives the CSS grid-rows collapse; inert keeps
  // a closed pod's controls out of the tab order).
  Ctl.prototype.setPodOpen = function (key, open) {
    var p = this.facetPod && this.facetPod[key]; if (!p) return;
    p.wrap.setAttribute('data-open', open ? 'true' : 'false');
    p.tg.setAttribute('aria-expanded', open ? 'true' : 'false');
    p.opts.inert = !open;
  };
  // Gold pill on a pod header: how many options in that facet are selected.
  Ctl.prototype.updateFacetSummary = function (key) {
    var p = this.facetPod && this.facetPod[key]; if (!p || !p.sumEl) return;
    var n = (this.state[key] && this.state[key].size) || 0;
    if (n > 0) { p.sumEl.textContent = String(n); p.sumEl.hidden = false; }
    else { p.sumEl.textContent = ''; p.sumEl.hidden = true; }
  };
  // In-place: update counts, checked + disabled state without touching structure.
  Ctl.prototype.syncFacets = function (view) {
    var counts = M.facetCounts(view, this.state, this.cfg), axes = this.axesForMode();
    for (var a = 0; a < axes.length; a++) {
      var ax = axes[a], rows = this.facetRows[ax.key]; if (!rows) continue;
      var cmap = counts[ax.key] || {};
      for (var v in rows) {
        var r = rows[v], checked = this.state[ax.key].has(v), c = cmap[v] || 0, off = (c === 0 && !checked);
        r.cb.checked = checked; r.cb.disabled = off;
        r.row.setAttribute('data-checked', checked ? 'true' : 'false');
        if (off) r.row.setAttribute('data-disabled', 'true'); else r.row.removeAttribute('data-disabled');
        r.countEl.textContent = String(c);
      }
      this.updateFacetSummary(ax.key);
    }
    this.applyFacetSearch();
  };
  Ctl.prototype.applyFacetSearch = function () {
    var q = this.facetSearch, any = 0;
    for (var axk in this.facetRows) {
      var rows = this.facetRows[axk], m = 0;
      for (var v in rows) {
        var show = !q || v.toLowerCase().indexOf(q) !== -1;
        rows[v].row.style.display = show ? '' : 'none';
        if (show && q) { m++; any++; }
      }
      // While searching, reveal pods that contain a match; otherwise restore each
      // pod's resting (user-controlled / collapsed) open state.
      if (q) this.setPodOpen(axk, m > 0);
      else this.setPodOpen(axk, !!this.facetRest[axk]);
    }
    if (this.nomatchEl) this.nomatchEl.hidden = !(q && any === 0);
  };

  // ---- chips + count -------------------------------------------------------
  Ctl.prototype.activeCount = function () { var n = 0, s = this.state; for (var k in s) if (s[k]) n += s[k].size; return n; };
  // Chips live in an inner flex wrapper so the outer container can play the
  // grid-rows (0fr↔1fr) height animation — the same technique as the pods.
  Ctl.prototype.chipHost = function () {
    if (!this.chipsEl) return null;
    if (!this._chipsIn) { this._chipsIn = ce('div', 'tcgf__chips-in'); this.chipsEl.appendChild(this._chipsIn); }
    return this._chipsIn;
  };
  // Incremental: keep existing chips (so they don't re-animate), remove gone ones,
  // add new ones (which fade in via CSS). No wholesale rebuild = no flicker.
  // When the LAST chip goes, it stays in the DOM to ride the row's slide-closed
  // animation and is purged after — removing it instantly would zero the content
  // height mid-transition and snap the pods below (the bounce this fixes).
  Ctl.prototype.renderChips = function () {
    var self = this, host = this.chipHost(); if (!host) return;
    // Flush any pending purge from a previous collapse so re-adding the same
    // filter within the animation window can't leave a zombie chip behind.
    if (this._chipsPurgeT) { clearTimeout(this._chipsPurgeT); this._chipsPurgeT = null; }
    if (this._chipsPurgeN) { this._chipsPurgeN.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); }); this._chipsPurgeN = null; }
    var desired = {};
    this.cfg.axes.forEach(function (ax) {
      (self.state[ax.key] ? Array.from(self.state[ax.key]) : []).forEach(function (v) { desired[ax.key + '' + v] = { ax: ax.key, v: v }; });
    });
    this._chipEls = this._chipEls || {};
    var n = this.activeCount(), gone = [];
    for (var k in this._chipEls) { if (!desired[k]) { gone.push(this._chipEls[k]); delete this._chipEls[k]; } }
    if (n === 0 && gone.length) {
      this._chipsPurgeN = gone;
      this._chipsPurgeT = setTimeout(function () {
        if (self.activeCount() === 0 && self._chipsPurgeN) self._chipsPurgeN.forEach(function (nd) { if (nd.parentNode) nd.parentNode.removeChild(nd); });
        self._chipsPurgeN = null; self._chipsPurgeT = null;
      }, 340);
    } else { gone.forEach(function (nd) { if (nd.parentNode) nd.parentNode.removeChild(nd); }); }
    for (var dk in desired) {
      if (this._chipEls[dk]) continue;
      var d = desired[dk];
      var chip = ce('span', 'tcgf__chip', esc(d.v) + ' <button type="button" aria-label="Remove ' + esc(d.v) + '">&times;</button>');
      (function (ax, v) { chip.querySelector('button').addEventListener('click', function () { self.state[ax]['delete'](v); self.expanded = {}; self.commit(); }); })(d.ax, d.v);
      host.appendChild(chip); this._chipEls[dk] = chip;
    }
    if (this.chipsEl) this.chipsEl.classList.toggle('tcgf__chips--on', n > 0); // slides the row open/closed (CSS)
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-tcgf-clear]'), function (btn) { btn.hidden = n === 0; });
    if (this.badgeEl) { this.badgeEl.hidden = n === 0; this.badgeEl.textContent = n ? String(n) : ''; }
  };
  Ctl.prototype.updateCount = function (plan, view) {
    var total = this.modeTotal(plan);
    var noun = this.mode === 'single' ? 'raw singles' : this.mode === 'slab' ? 'graded slabs' : 'sealed products';
    if (this.countEl) this.countEl.innerHTML = '<strong>' + total + '</strong> ' + noun + (this.indexed ? '' : ' <span class="tcgf__more">(indexing…)</span>');
    // Visible count updates every wave; the aria-live region only speaks for user
    // actions (not each background wave — that would spam a screen reader).
    this._liveText = total + ' ' + noun + ' shown';
    if (this.liveEl && !this._indexing && !this.firstRender) this.liveEl.textContent = this._liveText;
    var apply = this.root.querySelector('[data-tcgf-drawer-apply]');
    if (apply) apply.textContent = 'Show ' + total + ' result' + (total === 1 ? '' : 's');
  };

  // ---- era jump-strip ------------------------------------------------------
  Ctl.prototype.renderErabar = function (plan) {
    var self = this, bar = this.erabar; if (!bar) return;
    if (this.mode !== 'sealed' || plan.eras.length < 2) { bar.hidden = true; return; }
    bar.hidden = false;
    var sig = plan.eras.map(function (e) { return e.era; }).join('|');
    // Same era set → just refresh the counts in place (no rebuild / flicker).
    if (sig === this._eraSig && this._eraChips) {
      plan.eras.forEach(function (e) { var c = self._eraChips[e.era || '~']; if (c) c.count.textContent = e.total; });
      return;
    }
    this._eraSig = sig; this._eraChips = {}; bar.innerHTML = '';
    plan.eras.forEach(function (era) {
      var chip = ce('button', 'tcgf__era-jump', esc(era.era || 'More') + ' '); chip.type = 'button';
      var cs = ce('span', null, String(era.total)); cs.setAttribute('aria-hidden', 'true'); cs.style.opacity = '.6'; chip.appendChild(cs);
      chip.addEventListener('click', function () { var t = document.getElementById(self.eraId(era.era)); if (!t) return; var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; t.scrollIntoView({ behavior: rm ? 'auto' : 'smooth', block: 'start' }); });
      bar.appendChild(chip); self._eraChips[era.era || '~'] = { chip: chip, count: cs };
    });
  };

  // ---- URL sync ------------------------------------------------------------
  Ctl.prototype.readURL = function () {
    var p; try { p = new URLSearchParams(window.location.search); } catch (e) { return; }
    this.cfg.axes.forEach(function (ax) {
      var raw = p.get(ax.param); this.state[ax.key] = new Set();
      if (raw) raw.split(',').filter(Boolean).forEach(function (v) { this.state[ax.key].add(v); }, this);
    }, this);
    var m = p.get('mode'); if (m === 'single' || m === 'sealed') this.mode = m;
  };
  Ctl.prototype.writeURL = function () {
    var self = this; if (this.urlTimer) clearTimeout(this.urlTimer);
    this.urlTimer = setTimeout(function () {
      var p; try { p = new URLSearchParams(window.location.search); } catch (e) { return; }
      self.cfg.axes.forEach(function (ax) { p['delete'](ax.param); var v = Array.from(self.state[ax.key]); if (v.length) p.set(ax.param, v.join(',')); });
      p['delete']('mode'); if (self.mode === 'single') p.set('mode', 'single');
      var qs = p.toString();
      try { history.replaceState(history.state, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash); } catch (e) { /* sandboxed */ }
    }, DEBOUNCE);
  };
  // state changed by user → re-render + sync facet chip highlight + URL
  Ctl.prototype.commit = function () { this.render(); this.writeURL(); };

  // ---- mode + drawer + wiring ----------------------------------------------
  Ctl.prototype.setMode = function (m) {
    if (m === this.mode) return;
    var self = this, reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mode = m; this.expanded = {};
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-tcgf-mode]'), function (b) { b.setAttribute('aria-selected', b.getAttribute('data-tcgf-mode') === m ? 'true' : 'false'); });
    if (reduce || !this.mount || !window.matchMedia) { this.commit(); return; }
    // Crossfade the grid: fade current out, rebuild while hidden, fade new in.
    var mount = this.mount;
    if (this._modeTimer) clearTimeout(this._modeTimer);
    if (this._modeFadeTimer) clearTimeout(this._modeFadeTimer);
    this._modeSwitch = true;
    mount.style.transition = 'opacity .13s ease'; mount.style.opacity = '0';
    this._modeTimer = setTimeout(function () {
      try { self.render(); } finally { self._modeSwitch = false; } // never leave grid stuck hidden
      mount.style.opacity = '0';
      requestAnimationFrame(function () {
        mount.style.transition = 'opacity .22s ease'; mount.style.opacity = '1';
        self._modeFadeTimer = setTimeout(function () { mount.style.transition = ''; mount.style.opacity = ''; }, 260);
      });
      self.writeURL();
    }, 130);
  };
  Ctl.prototype.clearAll = function () {
    this.cfg.axes.forEach(function (ax) { this.state[ax.key] = new Set(); }, this);
    this.expanded = {}; this.facetSearch = ''; if (this.searchEl) this.searchEl.value = '';
    this.commit();
  };
  Ctl.prototype.openDrawer = function (o) {
    var root = this.root, sidebar = root.querySelector('[data-tcgf-sidebar]');
    root.setAttribute('data-drawer', o ? 'open' : 'closed');
    if (this.scrimEl) this.scrimEl.hidden = !o;
    document.documentElement.style.overflow = o ? 'hidden' : '';
    // Modal semantics: focus in, inert the rest of the section, restore on close.
    var rest = [root.querySelector('.tcgf__toolbar'), this.erabar, this.grid];
    if (o) {
      this._opener = document.activeElement;
      if (sidebar) { sidebar.setAttribute('role', 'dialog'); sidebar.setAttribute('aria-modal', 'true'); sidebar.setAttribute('aria-label', 'Filters'); }
      rest.forEach(function (el) { if (el) { try { el.inert = true; } catch (e) {} el.setAttribute('aria-hidden', 'true'); } });
      var close = root.querySelector('[data-tcgf-drawer-close]'); if (close) try { close.focus(); } catch (e) {}
    } else {
      if (sidebar) { sidebar.removeAttribute('role'); sidebar.removeAttribute('aria-modal'); }
      rest.forEach(function (el) { if (el) { try { el.inert = false; } catch (e) {} el.removeAttribute('aria-hidden'); } });
      if (this._opener && this._opener.focus) try { this._opener.focus(); } catch (e) {}
    }
  };

  Ctl.prototype.wire = function () {
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-tcgf-mode]'), function (b) { b.addEventListener('click', function () { self.setMode(b.getAttribute('data-tcgf-mode')); }); });
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-tcgf-clear]'), function (b) { b.addEventListener('click', function () { self.clearAll(); }); });
    if (this.searchEl) this.searchEl.addEventListener('input', function () { self.facetSearch = self.searchEl.value.trim().toLowerCase(); self.applyFacetSearch(); });
    var open = this.root.querySelector('[data-tcgf-facet-open]'); if (open) open.addEventListener('click', function () { self.openDrawer(true); });
    var close = this.root.querySelector('[data-tcgf-drawer-close]'); if (close) close.addEventListener('click', function () { self.openDrawer(false); });
    var apply = this.root.querySelector('[data-tcgf-drawer-apply]'); if (apply) apply.addEventListener('click', function () { self.openDrawer(false); });
    if (this.scrimEl) this.scrimEl.addEventListener('click', function () { self.openDrawer(false); });
    window.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.root.getAttribute('data-drawer') === 'open') self.openDrawer(false); });
    // Focus trap inside the open drawer.
    var sidebar = this.root.querySelector('[data-tcgf-sidebar]');
    if (sidebar) sidebar.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || self.root.getAttribute('data-drawer') !== 'open') return;
      var f = Array.prototype.filter.call(sidebar.querySelectorAll('button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'),
        function (el) { return !el.disabled && el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Crossing back to desktop with the drawer open would orphan overflow:hidden — close it.
    var mq = window.matchMedia && window.matchMedia('(max-width: 989px)');
    if (mq) { var onMq = function () { if (!mq.matches && self.root.getAttribute('data-drawer') === 'open') self.openDrawer(false); };
      if (mq.addEventListener) mq.addEventListener('change', onMq); else if (mq.addListener) mq.addListener(onMq); }
    window.addEventListener('popstate', function () { self.readURL(); self.expanded = {};
      Array.prototype.forEach.call(self.root.querySelectorAll('[data-tcgf-mode]'), function (b) { b.setAttribute('aria-selected', b.getAttribute('data-tcgf-mode') === self.mode ? 'true' : 'false'); });
      self.render(); });
  };

  // ---- boot ----------------------------------------------------------------
  Ctl.prototype.init = function () {
    if (!this.grid || !this.mount0()) return;
    this.collect(this.grid);
    this.pen = document.createDocumentFragment();
    for (var k in this.byId) this.pen.appendChild(this.byId[k].node);
    var stale = this.grid.querySelectorAll('.tcgf__era, .tcgf__pagination');
    for (var i = 0; i < stale.length; i++) stale[i].remove();
    this.grid.insertBefore(this.mount, this.emptyEl || null);
    this.readURL();
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-tcgf-mode]'), function (b) { b.setAttribute('aria-selected', b.getAttribute('data-tcgf-mode') === this.mode ? 'true' : 'false'); }, this);
    this.wire();
    this.initTopBtn();
    this.render();
    // Instant full load from this session's cache; otherwise index the tail live.
    if (this.loadCache()) { this.indexed = true; if (this.loadingEl) this.loadingEl.hidden = true; this.render(); }
    else { this.indexRemaining(); }
  };
  Ctl.prototype.mount0 = function () { this.mount = ce('div', 'tcgf__mount'); return true; };

  // ---- back to top -----------------------------------------------------------
  // Small fixed button that appears only after a DECISIVE upward scroll (>550px
  // accumulated) while deep in the page; hides on any downward scroll, near the
  // top, or after a few seconds idle — so it never gets in the way.
  Ctl.prototype.initTopBtn = function () {
    if (document.querySelector('.tcgf__top')) return; // one per page
    var btn = ce('button', 'tcgf__top',
      '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M12 4.5l-7 7 1.4 1.4 4.6-4.6V19.5h2V8.3l4.6 4.6 1.4-1.4z" fill="currentColor"/></svg>');
    btn.type = 'button'; btn.setAttribute('aria-label', 'Back to top'); btn.setAttribute('data-show', 'false');
    document.body.appendChild(btn);
    var lastY = window.pageYOffset || 0, up = 0, idleT = null, shown = false;
    function setShow(s) { if (s === shown) return; shown = s; btn.setAttribute('data-show', s ? 'true' : 'false'); }
    window.addEventListener('scroll', function () {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var dy = y - lastY; lastY = y;
      if (y < 300) { up = 0; setShow(false); return; } // near top: pointless, keep clear
      if (dy < 0) up += -dy;                            // accumulate upward intent
      else if (dy > 8) { up = 0; setShow(false); }      // heading down again: get out of the way
      if (up > 550) setShow(true);
      if (idleT) clearTimeout(idleT);
      idleT = setTimeout(function () { setShow(false); up = 0; }, 3500); // idle: fade away
    }, { passive: true });
    btn.addEventListener('click', function () {
      var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var y0 = window.pageYOffset || 0;
      try { window.scrollTo({ top: 0, behavior: rm ? 'auto' : 'smooth' }); } catch (e) { window.scrollTo(0, 0); }
      // If smooth scrolling is unsupported or parked (some browsers/tabs), jump.
      setTimeout(function () { var y = window.pageYOffset || 0; if (y > 0 && y > y0 * 0.7) window.scrollTo(0, 0); }, 650);
      setShow(false); up = 0;
    });
    this.topBtn = btn;
  };

  function ready() { var roots = document.querySelectorAll('[data-tcgf]'); for (var i = 0; i < roots.length; i++) { try { new Ctl(roots[i]).init(); } catch (e) { if (window.console) console.warn('[tcgf]', e); roots[i].setAttribute('data-js-active', 'false'); } } }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready); else ready();
})();
